import { Injectable } from "@angular/core";

import { firstValueFrom, Observable, ReplaySubject } from "rxjs";

import { Task, TaskTrack } from "../models/task.interface";


@Injectable()
export class LatestTaskHandlerService {

    private pending: TaskTrack<any> = null;
    private processing: boolean = false;

    public handleTask<T>(task: Task<T>): Promise<T> {
        return firstValueFrom(this.enqueueTask<T>(task));
    }

    private enqueueTask<T>(task: Task<T>): Observable<T> {
        const emitter: ReplaySubject<T> = new ReplaySubject<T>(1);

        // If there's already a pending task, discard it.
        if (this.pending) {
            this.pending.emitter.next(null);
            this.pending.emitter.complete();
        }

        // Set the new task as pending.
        this.pending = { task, emitter };

        // If there are no task processing, start processing this one.
        if (!this.processing) this.processNextTask().then();

        return emitter.asObservable();
    }

    private async processNextTask(): Promise<void> {
        if (!this.pending) return;

        this.processing = true;

        // Get the current pending task
        const { task, emitter } = this.pending;

        // Clear it immediately, so new tasks can be queued.
        this.pending = null;

        try {
            // Execute the task
            const result: any = await task();

            // Emit the result and close the emitter
            emitter.next(result);
            emitter.complete();

        } catch (error) {
            // Emit the error and close the emitter.
            emitter.error(error);
        }

        // Wait for debounce period before processing the next task.
        await new Promise(resolve => setTimeout(resolve, 100));

        if (!!this.pending) {
            // Process the next pending task if there is one.
            this.processNextTask().then();
        } else {
            // Otherwise, finish the processing.
            this.processing = false;
        }
    }
}

